// src/components/tutorials/JS_Number_Methods.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Number_Methods() {
  const refs = {
    toString: useRef(null),
    toExponential: useRef(null),
    toFixed: useRef(null),
    toPrecision: useRef(null),
    valueOf: useRef(null),
    isFinite: useRef(null),
    isInteger: useRef(null),
    isNaN: useRef(null),
    isSafeInteger: useRef(null),
    parseInt: useRef(null),
    parseFloat: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔢 JavaScript Number Methods</h1>
      <p className="tutorial-intro">
        JavaScript provides both <b>basic number methods</b> (usable on any
        number) and <b>static methods</b> (usable only via the Number object).
      </p>

      {/* Two-column Key Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
        }}
      >
        {/* Basic Methods */}
        <div>
          <h2 style={{ margin: 0, color: "#92400e" }}>Basic Methods</h2>
          <p>
            Basic number methods can be used on <b>any number</b>:
          </p>
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {[
              { key: "toString", label: "toString()" },
              { key: "toExponential", label: "toExponential()" },
              { key: "toFixed", label: "toFixed()" },
              { key: "toPrecision", label: "toPrecision()" },
              { key: "valueOf", label: "valueOf()" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Static Methods */}
        <div>
          <h2 style={{ margin: 0, color: "#92400e" }}>Static Methods</h2>
          <p>
            Static methods can only be used on <b>Number</b>:
          </p>
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {[
              { key: "isFinite", label: "Number.isFinite()" },
              { key: "isInteger", label: "Number.isInteger()" },
              { key: "isNaN", label: "Number.isNaN()" },
              { key: "isSafeInteger", label: "Number.isSafeInteger()" },
              { key: "parseInt", label: "Number.parseInt()" },
              { key: "parseFloat", label: "Number.parseFloat()" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 🔸 BASIC METHODS */}
      <h2 className="section-title" style={{ marginTop: "2rem" }}>
        🧩 Basic Number Methods
      </h2>
      <p>
        Basic number methods can be used on any number or Number object. They
        help with formatting, converting, and representing numbers.
      </p>

      {/* 1️⃣ toString */}
      <div ref={refs.toString} className="tutorial-section">
        <h3>1️⃣ toString()</h3>
        <p>
          Converts a number to a string. You can specify a base (radix) between
          2 and 36.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let num = 255;
console.log(num.toString()); // "255"
console.log(num.toString(2)); // "11111111"
console.log(num.toString(16)); // "ff"`}
          />
          <TryItButton
            code={`let num=255;console.log(num.toString());console.log(num.toString(2));console.log(num.toString(16));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>"255" "11111111" "ff"</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ toExponential */}
      <div ref={refs.toExponential} className="tutorial-section">
        <h3>2️⃣ toExponential()</h3>
        <p>
          Returns a string in exponential (scientific) notation with the
          specified number of digits.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let x = 9.656;
console.log(x.toExponential(2)); // "9.66e+0"
console.log(x.toExponential(4)); // "9.6560e+0"`}
          />
          <TryItButton
            code={`let x=9.656;console.log(x.toExponential(2));console.log(x.toExponential(4));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>"9.66e+0" "9.6560e+0"</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ toFixed */}
      <div ref={refs.toFixed} className="tutorial-section">
        <h3>3️⃣ toFixed()</h3>
        <p>Formats a number to a fixed number of decimal places.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let num = 9.656;
console.log(num.toFixed(0)); // "10"
console.log(num.toFixed(2)); // "9.66"
console.log(num.toFixed(4)); // "9.6560"`}
          />
          <TryItButton
            code={`let num=9.656;console.log(num.toFixed(0));console.log(num.toFixed(2));console.log(num.toFixed(4));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>"10" "9.66" "9.6560"</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ toPrecision */}
      <div ref={refs.toPrecision} className="tutorial-section">
        <h3>4️⃣ toPrecision()</h3>
        <p>
          Returns a string representing the number to a specified precision.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let x = 9.656;
console.log(x.toPrecision()); // "9.656"
console.log(x.toPrecision(2)); // "9.7"
console.log(x.toPrecision(4)); // "9.656"`}
          />
          <TryItButton
            code={`let x=9.656;console.log(x.toPrecision());console.log(x.toPrecision(2));console.log(x.toPrecision(4));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>"9.656" "9.7" "9.656"</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ valueOf */}
      <div ref={refs.valueOf} className="tutorial-section">
        <h3>5️⃣ valueOf()</h3>
        <p>Returns the primitive value of a Number object.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let num = new Number(42);
console.log(num.valueOf()); // 42`}
          />
          <TryItButton
            code={`let num=new Number(42);console.log(num.valueOf());`}
          />
        </div>
        <OutputBox>Output: 42</OutputBox>
      </div>

      {/* 🔸 STATIC METHODS */}
      <h2 className="section-title" style={{ marginTop: "2rem" }}>
        ⚙️ Static Number Methods
      </h2>
      <p>Static methods belong to the Number object.</p>

      {/* 6️⃣ isFinite */}
      <div ref={refs.isFinite} className="tutorial-section">
        <h3>6️⃣ Number.isFinite()</h3>
        <p>Checks if a value is a finite number.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.isFinite(100)); // true
console.log(Number.isFinite(Infinity)); // false`}
          />
          <TryItButton
            code={`console.log(Number.isFinite(100));console.log(Number.isFinite(Infinity));`}
          />
        </div>
        <OutputBox>Output: true, false</OutputBox>
      </div>

      {/* 7️⃣ isInteger */}
      <div ref={refs.isInteger} className="tutorial-section">
        <h3>7️⃣ Number.isInteger()</h3>
        <p>Checks whether a value is an integer.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.isInteger(10)); // true
console.log(Number.isInteger(10.5)); // false`}
          />
          <TryItButton
            code={`console.log(Number.isInteger(10));console.log(Number.isInteger(10.5));`}
          />
        </div>
        <OutputBox>Output: true, false</OutputBox>
      </div>

      {/* 8️⃣ isNaN */}
      <div ref={refs.isNaN} className="tutorial-section">
        <h3>8️⃣ Number.isNaN()</h3>
        <p>Determines if a value is exactly NaN.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.isNaN(NaN)); // true
console.log(Number.isNaN("NaN")); // false`}
          />
          <TryItButton
            code={`console.log(Number.isNaN(NaN));console.log(Number.isNaN("NaN"));`}
          />
        </div>
        <OutputBox>Output: true, false</OutputBox>
      </div>

      {/* 9️⃣ isSafeInteger */}
      <div ref={refs.isSafeInteger} className="tutorial-section">
        <h3>9️⃣ Number.isSafeInteger()</h3>
        <p>Checks whether a number is within the safe integer range.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.isSafeInteger(9007199254740991)); // true
console.log(Number.isSafeInteger(9007199254740992)); // false`}
          />
          <TryItButton
            code={`console.log(Number.isSafeInteger(9007199254740991));console.log(Number.isSafeInteger(9007199254740992));`}
          />
        </div>
        <OutputBox>Output: true, false</OutputBox>
      </div>

      {/* 10️⃣ parseInt */}
      <div ref={refs.parseInt} className="tutorial-section">
        <h3>🔟 Number.parseInt()</h3>
        <p>Parses a string and returns an integer.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.parseInt("42")); // 42
console.log(Number.parseInt("3.14")); // 3`}
          />
          <TryItButton
            code={`console.log(Number.parseInt("42"));console.log(Number.parseInt("3.14"));`}
          />
        </div>
        <OutputBox>Output: 42, 3</OutputBox>
      </div>

      {/* 11️⃣ parseFloat */}
      <div ref={refs.parseFloat} className="tutorial-section">
        <h3>1️⃣1️⃣ Number.parseFloat()</h3>
        <p>Parses a string and returns a floating-point number.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.parseFloat("3.14")); // 3.14
console.log(Number.parseFloat("42")); // 42`}
          />
          <TryItButton
            code={`console.log(Number.parseFloat("3.14"));console.log(Number.parseFloat("42"));`}
          />
        </div>
        <OutputBox>Output: 3.14, 42</OutputBox>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn <code>Math</code> object for mathematical operations.
          </li>
          <li>Understand floating-point precision issues (0.1 + 0.2 ≠ 0.3).</li>
          <li>
            Use <code>BigInt</code> for very large integers.
          </li>
        </ul>
      </div>
    </div>
  );
}
